import { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Link } from "react-router-dom";
import { MdAccountCircle, MdDashboard, MdHistory } from "react-icons/md";
import { FaRegAddressCard, FaSignOutAlt } from "react-icons/fa";
import { useHistory } from "react-router-dom";
import { API, Auth } from "aws-amplify";
import useAuthState from "../hooks/useAuthState";
import StoresIcon from "../images/store.svg";
import ProductsIcon from "../images/products.svg";
import OrdersIcon from "../images/shipping.svg";
import AvatarIcon from "../images/avatar.png";
import { Feature } from "flagged";
import { useTranslation } from "react-i18next";
import { clearStorage } from "../helpers/storage";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexFlow: "column",
    width: "95px",
    minWidth: "95px",
    height: "100%",
    zIndex: 1,
    boxShadow: "0px 0px 5px 0px rgba(0, 0, 0, 0.75)",

    "& #avatarIcon": {
      width: "40px",
      marginLeft: "-10px",
    },
    "& a": {
      textDecoration: "none",
    },
    "& #sidebarHeader": {
      paddingTop: "20px",
      paddingBottom: "30px",
    },
    "& #sidebarContent": {
      flex: "1 1 auto",
      display: "flex",
      flexDirection: "column",
    },
    "& .scrollArea": {
      flex: "1 1 auto",
      overflowY: "auto",
      paddingBottom: "60px",
    },
    "& .bottomDiv": {
      position: "absolute",
      bottom: "0",
      left: "0",
      right: "0",
      background: "white",
      zIndex: "1",
    },
    "& .languageOptions": {
      marginTop: "auto", // 👈 this pushes it to the bottom
      padding: "10px 0", // optional spacing
    },
    "& .sidebar-links": {
      padding: "10px 16px 10px 6px",
      marginBottom: "1px",
      fontSize: "20px",
      fontWeight: 700,
    },
    "& .sidebar-links.active": {
      paddingLeft: "1px",
      borderLeft: "5px solid white",
    },
    "& .link-img": {
      width: "28px",
    },
  },
}));

function Sidebar() {
  const classes = useStyles();
  const history = useHistory();
  const { setAuthStatus, User } = useAuthState();
  const [activeLink, setActiveLink] = useState("");
  const [UserGroup, setUserGroup] = useState("");
  const { t } = useTranslation();



  const ExportAllEmailAddresses = async () => {
    API.get("AE", `/Customers/GetExportCustomersLink`, { response: true })
      .then(async response => {
        const url = response.data.url;
        let parsedUrl: URL;
        try {
          parsedUrl = new URL(url);
        } catch {
          console.error("Export failed: received invalid URL");
          return;
        }
        if (parsedUrl.protocol !== "https:") {
          console.error("Export failed: URL must use HTTPS");
          return;
        }
        const link = document.createElement('a');
        link.href = parsedUrl.href;
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(error => {
        console.error(error);
      })

  };

  const signOut = () => {
    Auth.signOut()
      .then((data) => {
        clearStorage();
        setAuthStatus("noUser");
        history.push("/login");
      })
      .catch((err) => console.log(err));
  };


  useEffect(() => {
    if (User.attributes?.["custom:role"]) {
      setUserGroup(User.attributes["custom:role"]);
    }
  }, [User]);


  return (
    <div id="sidebar" className={classes.root}>
      <div id="sidebarHeader">
        <div className="flex-center-justify" id="avatar">
          <img id="avatarIcon" src={AvatarIcon} alt="Avatar" />
        </div>
      </div>
      <div id="sidebarContent">
        <Link
          title={t("sidebar.retaillocations")}
          to="/retail-locations"
          id="retailLocations"
          onClick={() => setActiveLink("/retail-locations")}
        >
          <div
            className={`sidebar-links ${activeLink === "/retail-locations" ? "active" : ""
              }`}
          >
            <div className="flex-center-justify">
              <img
                className="link-img"
                src={StoresIcon}
                alt="/retail-locations"
              />
            </div>
          </div>
        </Link>


        <Feature name="dashboard2">
          {UserGroup !== "replenishmentuser" ? (
            <Link
              title={t("sidebar.financialdashboard")}
              to="/financial-dashboard"
              id="financialDashboard"
              onClick={() => setActiveLink("/financial-dashboard")}
            >
              <div
                className={`sidebar-links ${activeLink === "/financial-dashboard" ? "active" : ""
                  }`}
              >
                <div className="flex-center-justify">
                  <MdDashboard size={24} />
                </div>
              </div>
            </Link>
          ) : null}
        </Feature>

        {UserGroup !== "replenishmentuser" ? (
          <Link
            title={t("sidebar.productmanagement")}
            to="/product-management"
            id="productManagement"
            onClick={() => setActiveLink("/product-management")}
          >
            <div
              className={`sidebar-links ${activeLink === "/product-management" ? "active" : ""
                }`}
            >
              <div className="flex-center-justify">
                <img
                  className="link-img"
                  src={ProductsIcon}
                  alt="/product-management"
                />
              </div>
            </div>
          </Link>
        ) : null}

        <Feature name="collectorders">
          {UserGroup !== "replenishmentuser" ? (
            <Link
              title={t("sidebar.orders")}
              to="/orders"
              id="orders"
              onClick={() => setActiveLink("/orders")}
            >
              <div
                className={`sidebar-links ${activeLink === "/orders" ? "active" : ""
                  }`}
              >
                <div className="flex-center-justify">
                  <img
                    className="link-img"
                    src={OrdersIcon}
                    alt="/orders"
                  />
                </div>
              </div>
            </Link>
          ) : null}
        </Feature>

        {UserGroup !== "replenishmentuser" ? (
          <Link
            title={t("sidebar.usermanagement")}
            to="/user-management"
            id="userManagement"
            onClick={() => setActiveLink("/user-management")}
          >
            <div
              className={`sidebar-links ${activeLink === "/user-management" ? "active" : ""
                }`}
            >
              <div className="flex-center-justify">
                <MdAccountCircle size={24} />
              </div>
            </div>
          </Link>
        ) : null}

        <Feature name="barcodescanpromotionemail">
          <div className={`sidebar-links`}>
            <div className="flex-center-justify">
              <FaRegAddressCard size={24} onClick={ExportAllEmailAddresses} style={{ cursor: "pointer" }} />
            </div>
          </div>
        </Feature>

        <Link title={t("sidebar.logout")} onClick={() => signOut()} to="/">
          <div className={`sidebar-links`}>
            <div className="flex-center-justify">
              <FaSignOutAlt size={24} />
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}

export default Sidebar;
